import { useSaudiListDateRange } from '../../products/saudi/hooks/useSaudiListDateRange';
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { makePageT } from '../../shared/utils/pageI18n';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { PackageCheck, Search, Plus, Trash2, RefreshCcw, CheckCircle, AlertTriangle, AlertCircle, Calendar, ChevronDown, ChevronUp, Upload, Download, FileSpreadsheet, FileOutput, Filter, Eye, X, XCircle, LayoutGrid, Layers, Clock, List, LayoutList, FileText, Building2, DollarSign, Package } from 'lucide-react';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import { useColumnHeaderFilters } from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, textColumnRule, exactColumnRule } from '../../shared/utils/columnHeaderFilters';
import localApiAdapter from '../../core/services/localApiAdapter';
import CustomSelect from '../../shared/components/custom/CustomSelect';
import { exportToCSV, exportToExcel, triggerFileInput, importFromFile } from '../../shared/utils/importExportUtils';
import { generateReportPDF } from '../../shared/utils/reportPdfGenerator';
import { generateReportExcel } from '../../shared/utils/reportExcelGeneratorAdvanced';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import '../../shared/styles/GPSTrackingDatePicker.css';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import { formatBrandMoney, getBrandLocale, getBrandDatePickerLocale } from '../../shared/utils/brandCurrency';
import { isSaudiBrand } from '../../products/saudi/utils/isSaudiBrand';
import FacturationListPageLayout, {
  facturationTableScrollClass,
} from '../facturation/components/FacturationListPageLayout';
import FacturationViewModeToggle from '../facturation/components/FacturationViewModeToggle';
import { wrapControlsStripSearch } from '../../shared/utils/listPageSearchVisibility';
import { useGlobalTheme } from '../../shared/hooks/useGlobalTheme';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import QawaemTableCheckbox from '../../products/saudi/components/QawaemTableCheckbox';
import AchatsFormModalTabs from './components/AchatsFormModalTabs';
import { buildAchatsColumnDefs, buildAchatsImportExportProps, achatsColHeaderProps, ACHATS_FORM_MODAL_SHELL, ACHATS_FORM_TAB_PANEL, ACHATS_FORM_GRID, useAchatsModalTheme, useAchatsColumnVisibility, achatsTableColSpan, ACHATS_TABLE_ACTIONS_TH_CLASS, useAchatsRowSelection, buildAchatsBulkDeleteFilterAction, AchatsBulkDeleteBar, useAppNotificationToast, AchatsCrudNotificationToast, sortRecordsNewestFirst, notifyCreated, notifyUpdated, notifyDeleted, notifyBulkDeleted, notifyError, resolveAchatsEntityReference, AchatsConfigNotice, fetchAchatsProducts, fetchAchatsWarehouses, fetchAchatsSuppliers, buildAchatsProductSelectOptions, mapAchatsProductSelection, ensureAchatsDatePickerLocales, useAchatsListViewMode, useAchatsListDisplay, achatsSettingsFromConfig } from './achatsListPageUtils';
import { useAchatsConfig } from './useAchatsConfig';
import QawaemErpCrudModal, { QawaemErpViewModal } from '../../products/saudi/components/QawaemErpCrudModal';
import RequiredLabelText from '../../shared/components/custom/RequiredLabelText';
import { mergeStickySelectClass } from '../../products/saudi/components/QawaemTableStickyActions';

ensureAchatsDatePickerLocales();

const ReceptionsAchatsPage = () => {
  const { t, i18n } = useTranslation();
  const p = makePageT(t, 'modules.achats.pages.receptionsAchatsPage');
  const entityLabel = p('entity_label', { defaultValue: p('title') });
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const dateLocale = getBrandDatePickerLocale();
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString(getBrandLocale(i18n.language)) : '-';
  const qawaem = isSaudiBrand();
  const { isDashboardLightMode } = useGlobalTheme();
  const { cfgTheme, modal: m } = useAchatsModalTheme();
  const achatsConfig = useAchatsConfig();
  const achatsSettings = achatsSettingsFromConfig(achatsConfig);

  const statusOptions = [
    { value: 'pending', label: p('status_pending'), icon: Clock, style: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
    { value: 'partial', label: p('status_partial'), icon: AlertCircle, style: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
    { value: 'complete', label: p('status_complete'), icon: CheckCircle, style: 'bg-green-500/20 text-green-400 border-green-500/30' },
    { value: 'rejected', label: p('status_rejected'), icon: XCircle, style: 'bg-red-500/20 text-red-400 border-red-500/30' }
  ];

  const qualityCheckOptions = [
    { value: 'pending', label: p('quality_pending') },
    { value: 'passed', label: p('quality_passed') },
    { value: 'failed', label: p('quality_failed') },
    { value: 'partial', label: p('quality_partial') },
  ];

  const getQualityLabel = (value) => qualityCheckOptions.find((o) => o.value === value)?.label || value || '-';

  const mainTabs = [
    { id: 'all_receipts', name: p('mainTab_allReturns'), icon: LayoutGrid },
    { id: 'par_statut', name: p('mainTab_byStatus'), icon: AlertCircle },
    { id: 'par_qualite', name: p('mainTab_byReason'), icon: FileText }
  ];

  const quickFilterButtons = [
    { id: 'all', name: p('quickFilter_all'), icon: Layers },
    { id: 'pending', name: p('quickFilter_pending'), icon: Clock },
    { id: 'partial', name: p('quickFilter_partial'), icon: AlertCircle },
    { id: 'complete', name: p('quickFilter_complete'), icon: Package },
  ];

  useEffect(() => {
    const handleClickOutside = (event) => {
      const isClickOnStatusButton = event.target.closest('[data-status-dropdown-button]');
      const isClickInStatusDropdown = event.target.closest('[data-status-dropdown]');
      if (!isClickOnStatusButton && !isClickInStatusDropdown) {
        setShowStatusDropdown(false);
      }
      
      const isClickOnReasonButton = event.target.closest('[data-reason-dropdown-button]');
      const isClickInReasonDropdown = event.target.closest('[data-reason-dropdown]');
      if (!isClickOnReasonButton && !isClickInReasonDropdown) {
        setShowReasonDropdown(false);
      }
      
      const isClickOnRefundButton = event.target.closest('[data-refund-dropdown-button]');
      const isClickInRefundDropdown = event.target.closest('[data-refund-dropdown]');
      if (!isClickOnRefundButton && !isClickInRefundDropdown) {
        setShowRefundDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const [searchTerm, setSearchTerm] = useState('');
  const [receipts, setReceipts] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [viewMode, setViewMode] = useAchatsListViewMode(achatsConfig);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingRecord, setViewingRecord] = useState(null);
  
  const [formTab, setFormTab] = useState('general');
  const formTabs = useMemo(() => ([
    { id: 'general', label: p('section_return_info'), icon: PackageCheck, iconColor: 'text-blue-400' },
    { id: 'details', label: p('section_refund_notes'), icon: DollarSign, iconColor: 'text-green-400' },
  ]), [p]);
  const [formData, setFormData] = useState({
    receipt_number: '',
    purchase_order_id: '',
    purchase_order_number: '',
    supplier_id: '',
    supplier_name: '',
    receipt_date: new Date().toISOString().split('T')[0],
    product_id: '',
    product_name: '',
    product_code: '',
    ordered_quantity: '',
    received_quantity: '',
    rejected_quantity: '',
    unit_price: '',
    total_value: '',
    status: 'pending',
    quality_check_status: 'pending',
    warehouse_id: '',
    warehouse_name: '',
    notes: ''
  });
  
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const { dateRange, setDateRange } = useSaudiListDateRange();
  const [isDateRangeOpen, setIsDateRangeOpen] = useState(false);
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showReasonDropdown, setShowReasonDropdown] = useState(false);
  const [showRefundDropdown, setShowRefundDropdown] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('all_receipts');
  const [quickFilter, setQuickFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isStatusFilterOpen, setIsStatusFilterOpen] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isGeneratingExcel, setIsGeneratingExcel] = useState(false);
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);
  
  // Dropdown data states
  const [availableSuppliers, setAvailableSuppliers] = useState([]);
  const [availablePurchaseOrders, setAvailablePurchaseOrders] = useState([]);
  const [availableProducts, setAvailableProducts] = useState([]);
  const [availableWarehouses, setAvailableWarehouses] = useState([]);
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [loadingWarehouses, setLoadingWarehouses] = useState(false);
  
  const dateRangeRef = useRef(null);
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  const statusDropdownRef = useRef(null);
  const reasonDropdownRef = useRef(null);
  const refundDropdownRef = useRef(null);
  const statusFilterRef = useRef(null);
  const { filters: colFilters, setFilter: setColFilter, editingKey, setEditingKey } = useColumnHeaderFilters();

  useEffect(() => { 
    loadReceipts(); 
    loadSuppliers();
    loadPurchaseOrders();
    loadProducts();
    loadWarehouses();
  }, []);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dateRangeRef.current && !dateRangeRef.current.contains(event.target)) setIsDateRangeOpen(false);
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) setShowImportDropdown(false);
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) setShowExportDropdown(false);
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target)) setShowStatusDropdown(false);
      if (statusFilterRef.current && !statusFilterRef.current.contains(event.target)) setIsStatusFilterOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadReceipts = async () => {
    try {
      const response = await localApiAdapter.get('/achats/receptions');
      setReceipts(sortRecordsNewestFirst(response?.data || response || []));
    } catch (error) {
      console.error('Error loading purchase receipts:', error);
      setReceipts([]);
    }
  };

  const loadSuppliers = async () => {
    try {
      setLoadingSuppliers(true);
      setAvailableSuppliers(await fetchAchatsSuppliers());
    } catch (error) {
      console.error('Error loading suppliers:', error);
      setAvailableSuppliers([]);
    } finally {
      setLoadingSuppliers(false);
    }
  };

  const loadPurchaseOrders = async () => {
    try {
      setLoadingOrders(true);
      const response = await localApiAdapter.get('/achats/purchase-orders');
      setAvailablePurchaseOrders(response?.data || response || []);
    } catch (error) {
      console.error('Error loading purchase orders:', error);
      setAvailablePurchaseOrders([]);
    } finally {
      setLoadingOrders(false);
    }
  };

  const loadProducts = async () => {
    try {
      setLoadingProducts(true);
      const products = await fetchAchatsProducts();
      setAvailableProducts(products);
    } catch (error) {
      console.error('Error loading products:', error);
      setAvailableProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  };

  const loadWarehouses = async () => {
    try {
      setLoadingWarehouses(true);
      const warehouses = await fetchAchatsWarehouses();
      setAvailableWarehouses(warehouses);
    } catch (error) {
      console.error('Error loading warehouses:', error);
      setAvailableWarehouses([]);
    } finally {
      setLoadingWarehouses(false);
    }
  };

  const productOptions = useMemo(
    () => buildAchatsProductSelectOptions(availableProducts, []),
    [availableProducts],
  );

  const handleSupplierSelection = (supplierId) => {
    if (!supplierId) {
      setFormData(prev => ({ ...prev, supplier_id: '', supplier_name: '' }));
      return;
    }
    const supplier = availableSuppliers.find(s => s.id.toString() === supplierId.toString());
    if (supplier) {
      setFormData(prev => ({ 
        ...prev, 
        supplier_id: supplierId,
        supplier_name: supplier.company_name || supplier.name || ''
      }));
    }
  };

  const handleOrderSelection = (orderId) => {
    if (!orderId) {
      setFormData(prev => ({ ...prev, purchase_order_id: '', purchase_order_number: '' }));
      return;
    }
    const order = availablePurchaseOrders.find(o => o.id.toString() === orderId.toString());
    if (order) {
      setFormData(prev => ({ 
        ...prev, 
        purchase_order_id: orderId,
        purchase_order_number: order.order_number || '',
        supplier_id: order.supplier_id || '',
        supplier_name: order.supplier_name || '',
        total_value: order.total_amount || ''
      }));
    }
  };

  const handleProductSelection = (productId) => {
    if (!productId) {
      setFormData((prev) => ({ ...prev, product_id: '', product_name: '', product_code: '' }));
      return;
    }
    const product = availableProducts.find((item) => String(item.id) === String(productId));
    if (product) {
      const patch = mapAchatsProductSelection(product, []);
      setFormData((prev) => {
        const received = parseFloat(prev.received_quantity) || 0;
        const unitPrice = parseFloat(patch.unit_price) || 0;
        return {
          ...prev,
          ...patch,
          total_value: received && unitPrice ? (received * unitPrice).toFixed(2) : prev.total_value,
        };
      });
    }
  };

  const handleWarehouseSelection = (warehouseId) => {
    if (!warehouseId) {
      setFormData((prev) => ({ ...prev, warehouse_id: '', warehouse_name: '' }));
      return;
    }
    const warehouse = availableWarehouses.find((w) => String(w.id) === String(warehouseId));
    if (warehouse) {
      setFormData((prev) => ({
        ...prev,
        warehouse_id: warehouse.id?.toString() || warehouseId,
        warehouse_name: warehouse.name || warehouse.warehouse_name || '',
      }));
    }
  };

  const stats = {
    total: receipts.length,
    pending: receipts.filter(r => r.status === 'pending').length,
    partial: receipts.filter(r => r.status === 'partial').length,
    complete: receipts.filter(r => r.status === 'complete').length,
    totalAmount: receipts.reduce((sum, r) => sum + (parseFloat(r.total_value) || 0), 0),
  };


  const returnDistribution = {
    all_receipts: {
      title: p('dist_allReturnsTitle'),
      data: [
        { filterKey: 'pending', name: p('dist_pending'), count: stats.pending, color: "bg-gradient-to-r from-amber-500 to-amber-600" },
        { filterKey: 'partial', name: p('status_partial'), count: stats.partial, color: "bg-gradient-to-r from-blue-500 to-blue-600" },
        { filterKey: 'complete', name: p('status_complete'), count: stats.complete, color: "bg-gradient-to-r from-green-500 to-green-600" }
      ]
    },
    par_statut: {
      title: p('dist_byStatusTitle'),
      data: [
        { filterKey: 'in_progress', name: p('dist_inProgress'), count: stats.pending + stats.partial, color: "bg-gradient-to-r from-blue-500 to-blue-600" },
        { filterKey: 'finished', name: p('dist_finished'), count: stats.complete, color: "bg-gradient-to-r from-green-500 to-green-600" }
      ]
    },
    par_qualite: {
      title: p('dist_byReasonTitle'),
      data: (() => {
        const qualityCounts = {
          pending: receipts.filter(r => r.quality_check_status === 'pending').length,
          passed: receipts.filter(r => r.quality_check_status === 'passed').length,
          failed: receipts.filter(r => r.quality_check_status === 'failed').length,
        };
        return [
          { filterKey: 'pending', name: p('quality_pending'), count: qualityCounts.pending, color: "bg-gradient-to-r from-amber-500 to-amber-600" },
          { filterKey: 'passed', name: p('quality_passed'), count: qualityCounts.passed, color: "bg-gradient-to-r from-green-500 to-green-600" },
          { filterKey: 'failed', name: p('quality_failed'), count: qualityCounts.failed, color: "bg-gradient-to-r from-red-500 to-red-600" },
        ];
      })()
    }
  };

  const distributionInfo = returnDistribution[mainViewMode] || returnDistribution.all_receipts;

  const baseFilteredReceipts = receipts.filter(r => {
    if (dateRange.start || dateRange.end) {
      const returnDate = new Date(r.receipt_date);
      
      if (dateRange.start) {
        const startOfDay = new Date(dateRange.start);
        startOfDay.setHours(0, 0, 0, 0);
        if (returnDate < startOfDay) return false;
      }
      
      if (dateRange.end) {
        const endOfDay = new Date(dateRange.end);
        endOfDay.setHours(23, 59, 59, 999);
        if (returnDate > endOfDay) return false;
      }
    }
    
    if (statusFilter !== 'all' && r.status !== statusFilter) return false;
    if (searchTerm && !r.receipt_number?.toLowerCase().includes(searchTerm.toLowerCase()) && !r.supplier_name?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    
    if (mainViewMode === 'all_receipts') {
      if (quickFilter === 'pending' && r.status !== 'pending') return false;
      if (quickFilter === 'partial' && r.status !== 'partial') return false;
      if (quickFilter === 'complete' && r.status !== 'complete') return false;
    }
    
    if (activeDistributionFilter) {
      switch(mainViewMode) {
        case 'all_receipts':
          if (activeDistributionFilter === 'pending' && r.status !== 'pending') return false;
          if (activeDistributionFilter === 'partial' && r.status !== 'partial') return false;
          if (activeDistributionFilter === 'complete' && r.status !== 'complete') return false;
          break;
        case 'par_statut':
          if (activeDistributionFilter === 'in_progress' && !['pending', 'partial'].includes(r.status)) return false;
          if (activeDistributionFilter === 'finished' && r.status !== 'complete') return false;
          break;
        case 'par_qualite':
          if (r.quality_check_status !== activeDistributionFilter) return false;
          break;
      }
    }
    
    return true;
  });

  const columnFilterRules = useMemo(
    () => [
      textColumnRule('receipt_number'),
      textColumnRule('supplier_name'),
      textColumnRule('receipt_date', (r) => (r.receipt_date ? fmtDate(r.receipt_date) : '')),
      textColumnRule('purchase_order_number'),
      textColumnRule('total_value', (r) => String(r.total_value ?? '')),
      textColumnRule('quantities', (r) => `${r.received_quantity ?? ''}/${r.ordered_quantity ?? ''}`),
      exactColumnRule('status', (r) => r.status || ''),
      textColumnRule('warehouse', (r) => r.warehouse_name || r.warehouse || ''),
    ],
    [i18n.language],
  );

  const { filtered: filteredReceipts, displayed: displayedReceipts } = useAchatsListDisplay(
    baseFilteredReceipts,
    colFilters,
    columnFilterRules,
    achatsConfig.itemsPerPage,
  );

  const rowSelection = useAchatsRowSelection(selectedRecords, filteredReceipts);

  const colHeader = (columnKey, label, opts = {}) =>
    achatsColHeaderProps({ columnKey, label, colFilters, setColFilter, editingKey, setEditingKey, ...opts });

  const generateReceiptNumber = useCallback(
    () => resolveAchatsEntityReference(achatsSettings, 'purchaseReceipt', 'REC', receipts, (r) => r.receipt_number),
    [achatsSettings, receipts],
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const received = parseFloat(formData.received_quantity) || 0;
      const ordered = parseFloat(formData.ordered_quantity) || 0;
      const unitPrice = parseFloat(formData.unit_price) || 0;

      if (!achatsConfig.allowPartialReceipts && ordered > 0 && received > 0 && received < ordered) {
        notifyError(showNotify, p('partial_receipts_disabled', { defaultValue: 'الاستلام الجزئي غير مسموح — فعّله من إعدادات المشتريات.' }));
        return;
      }

      if (achatsConfig.qualityCheckRequired && received > 0 && formData.quality_check_status === 'pending') {
        notifyError(showNotify, p('quality_check_required', { defaultValue: 'أكمل مراقبة الجودة قبل تأكيد الاستلام.' }));
        return;
      }

      const receiptNumber = resolveAchatsEntityReference(
        achatsSettings,
        'purchaseReceipt',
        'REC',
        receipts,
        (r) => r.receipt_number,
        formData.receipt_number,
      );
      const submitData = {
        receipt_number: receiptNumber,
        supplier_id: formData.supplier_id || null,
        supplier_name: formData.supplier_name,
        purchase_order_id: formData.purchase_order_id || null,
        purchase_order_number: formData.purchase_order_number || null,
        receipt_date: formData.receipt_date || new Date().toISOString().split('T')[0],
        product_id: formData.product_id ? parseInt(formData.product_id, 10) || null : null,
        product_name: formData.product_name,
        product_code: formData.product_code || null,
        ordered_quantity: parseFloat(formData.ordered_quantity) || 0,
        received_quantity: received,
        rejected_quantity: parseFloat(formData.rejected_quantity) || 0,
        unit_price: unitPrice,
        total_value: parseFloat(formData.total_value) || received * unitPrice,
        status: formData.status || (ordered > 0 && received > 0 && received < ordered && achatsConfig.allowPartialReceipts ? 'partial' : 'pending'),
        quality_check_status: formData.quality_check_status || (achatsConfig.qualityCheckRequired ? 'pending' : 'passed'),
        warehouse_id: formData.warehouse_id || null,
        warehouse_name: formData.warehouse_name || null,
        notes: formData.notes || null,
      };

      if (editingRecord) {
        await localApiAdapter.put(`/achats/receptions/${editingRecord.id}`, submitData);
        notifyUpdated(showNotify, entityLabel);
      } else {
        await localApiAdapter.post('/achats/receptions', submitData);
        notifyCreated(showNotify, entityLabel);
      }
    } catch (error) {
      console.error('Error saving:', error);
      notifyError(showNotify, p('save_error', { message: error.message || p('save_error_unknown') }));
    } finally {
      resetForm();
      loadReceipts();
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setFormData({
      receipt_number: record.receipt_number || '',
      supplier_id: record.supplier_id?.toString() || '',
      supplier_name: record.supplier_name || '',
      purchase_order_id: record.purchase_order_id?.toString() || '',
      purchase_order_number: record.purchase_order_number || '',
      receipt_date: record.receipt_date || '',
      product_id: record.product_id?.toString() || '',
      product_name: record.product_name || '',
      product_code: record.product_code || '',
      ordered_quantity: record.ordered_quantity?.toString() || '',
      received_quantity: record.received_quantity?.toString() || '',
      rejected_quantity: record.rejected_quantity?.toString() || '',
      unit_price: record.unit_price?.toString() || '',
      total_value: record.total_value?.toString() || '',
      status: record.status || 'pending',
      quality_check_status: record.quality_check_status || 'pending',
      warehouse_id: record.warehouse_id?.toString() || '',
      warehouse_name: record.warehouse_name || '',
      notes: record.notes || '',
    });
    setFormTab('general');
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(p('delete_confirm'))) {
      try {
        await localApiAdapter.delete(`/achats/receptions/${id}`);
        notifyDeleted(showNotify, entityLabel);
        loadReceipts();
      } catch (error) {
        console.error('Error:', error);
        notifyError(showNotify, p('save_error', { message: error.message || p('save_error_unknown') }));
      }
    }
  };

  const handleBulkDelete = async () => {
    if (window.confirm(p('bulk_delete_confirm', { count: selectedRecords.length }))) {
      try {
        const deleteCount = selectedRecords.length;
        await Promise.all(selectedRecords.map(id => localApiAdapter.delete(`/achats/receptions/${id}`)));
        notifyBulkDeleted(showNotify, deleteCount, entityLabel);
        setSelectedRecords([]);
        loadReceipts();
      } catch (error) {
        console.error('Error deleting records:', error);
        notifyError(showNotify, p('save_error', { message: error.message || p('save_error_unknown') }));
      }
    }
  };

  const resetForm = () => {
    setFormData({
      receipt_number: '',
      purchase_order_id: '',
      purchase_order_number: '',
      supplier_id: '',
      supplier_name: '',
      receipt_date: new Date().toISOString().split('T')[0],
      product_id: '',
      product_name: '',
      product_code: '',
      ordered_quantity: '',
      received_quantity: '',
      rejected_quantity: '',
      unit_price: '',
      total_value: '',
      status: 'pending',
      quality_check_status: 'pending',
      warehouse_id: '',
      warehouse_name: '',
      notes: ''
    });
    setEditingRecord(null);
    setFormTab('general');
    setShowModal(false);
  };

  const handleExport = () => {
    const headers = [
      { key: 'receipt_number', label: p('col_receipt_number') },
      { key: 'supplier_name', label: p('col_supplier') },
      { key: 'receipt_date', label: p('col_receipt_date') },
      { key: 'total_value', label: p('col_amount') },
      { key: 'quality_check_status', label: p('col_reason') },
      { key: 'status', label: p('col_status') },
      { key: 'warehouse_name', label: p('export_warehouse_name') }
    ];

    const fileName = `receptions_achats_${new Date().toISOString().split('T')[0]}`;
    try {
      exportToCSV(filteredReceipts, headers, `${fileName}.csv`);
    } catch (error) {
      console.error('Export error:', error);
    }
  };

  const handleExportPDF = async () => {
    try {
      setIsGeneratingPDF(true);
      const filteredData = filteredReceipts;
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = `
        <div class="report-content">
          <table>
            <thead>
              <tr>
                <th>${p('col_receipt_number')}</th>
                <th>${p('col_supplier')}</th>
                <th>${p('label_date').replace(':', '')}</th>
                <th>${p('col_amount')}</th>
                <th>${p('col_status')}</th>
              </tr>
            </thead>
            <tbody>
              ${filteredData.map(item => `
                <tr>
                  <td>${item.receipt_number || 'N/A'}</td>
                  <td>${item.supplier_name || 'N/A'}</td>
                  <td>${fmtDate(item.receipt_date)}</td>
                  <td>${formatBrandMoney((item.total_value || 0))}</td>
                  <td>${item.status || 'N/A'}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
      `;
      document.body.appendChild(tempDiv);
      await generateReportPDF(p('report_pdf_title'), {});
      document.body.removeChild(tempDiv);
    } catch (error) {
      console.error('PDF generation error:', error);
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handleExportExcel = async () => {
    try {
      setIsGeneratingExcel(true);
      const filteredData = filteredReceipts;
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = `
        <div class="report-content">
          <table>
            <thead>
              <tr>
                <th>${p('col_receipt_number')}</th>
                <th>${p('col_supplier')}</th>
                <th>${p('col_receipt_date')}</th>
                <th>${p('col_amount')}</th>
                <th>${p('col_reason')}</th>
                <th>${p('col_status')}</th>
                <th>${p('col_refund')}</th>
              </tr>
            </thead>
            <tbody>
              ${filteredData.map(item => `
                <tr>
                  <td>${item.receipt_number || ''}</td>
                  <td>${item.supplier_name || ''}</td>
                  <td>${fmtDate(item.receipt_date)}</td>
                  <td>${formatBrandMoney(item.total_value || 0)}</td>
                  <td>${item.quality_check_status || ''}</td>
                  <td>${item.status || ''}</td>
                  <td>${item.warehouse_name || ''}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
      `;
      document.body.appendChild(tempDiv);
      await generateReportExcel(p('report_excel_title'), p('report_pdf_title'), {});
      document.body.removeChild(tempDiv);
    } catch (error) {
      console.error('Excel generation error:', error);
    } finally {
      setIsGeneratingExcel(false);
    }
  };

  const handleImport = () => {
    const headers = [
      { key: 'receipt_number', label: p('col_receipt_number') },
      { key: 'supplier_name', label: p('col_supplier') },
      { key: 'receipt_date', label: p('col_receipt_date') },
      { key: 'total_value', label: p('col_amount') }
    ];

    const schema = {
      requiredFields: ['supplier_name', 'receipt_date'],
      validators: {
        total_value: (value) => !value || !isNaN(parseFloat(value))
      }
    };

    triggerFileInput(async (file) => {
      try {
        const result = await importFromFile(file, headers, schema);
        
        if (result.success) {
          let successCount = 0;
          for (const record of result.data) {
            try {
              await localApiAdapter.post('/achats/receptions', record);
              successCount++;
            } catch (err) {
              console.error('Import record error:', err);
            }
          }
          await loadReceipts();
          alert(p('import_success', { total: result.totalCount, imported: successCount }));
        }
      } catch (error) {
        console.error('Import error:', error);
      }
    }, '.csv,.xlsx,.xls');
  };


  const handleSelectAll = () => {
    if (rowSelection.allRowsSelected) {
      setSelectedRecords([]);
    } else {
      setSelectedRecords(filteredReceipts.map((r) => r.id));
    }
  };

  const getStatusBadge = (status) => {
    const config = statusOptions.find(s => s.value === status) || statusOptions[0];
    const { label, icon: Icon, style } = config;
    return <span className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 border ${style}`}><Icon size={14} />{label}</span>;
  };


  const listStats = useMemo(
    () => [
      { key: 'total', label: p('stat_total_returns'), value: stats.total, icon: PackageCheck, color: 'from-blue-500 to-blue-600', variant: 'copper' },
      { key: 'pending', label: p('stat_pending'), value: stats.pending, icon: Clock, color: 'from-amber-500 to-amber-600', variant: 'forest' },
      { key: 'partial', label: p('stat_partial'), value: stats.partial, icon: CheckCircle, color: 'from-blue-500 to-blue-600', variant: 'copper' },
      { key: 'complete', label: p('stat_complete'), value: stats.complete, icon: Package, color: 'from-green-500 to-green-600', variant: 'forest' },
    ],
    [stats, p],
  );

  const receptionsColumnKeys = useMemo(
    () => ['receipt_number', 'supplier_name', 'receipt_date', 'purchase_order_number', 'total_value', 'quantities', 'status', 'warehouse', 'actions'],
    [],
  );
  const { columnVisibility, handleToggleColumn, isColumnVisible } = useAchatsColumnVisibility(receptionsColumnKeys);

  const qawaemColumns = useMemo(
    () => buildAchatsColumnDefs([
      { key: 'receipt_number', labelKey: 'col_receipt_number' },
      { key: 'supplier_name', labelKey: 'col_supplier' },
      { key: 'receipt_date', labelKey: 'col_receipt_date' },
      { key: 'purchase_order_number', labelKey: 'col_purchase_order' },
      { key: 'total_value', labelKey: 'col_amount' },
      { key: 'quantities', labelKey: 'col_quantities' },
      { key: 'status', labelKey: 'col_status' },
      { key: 'warehouse', labelKey: 'col_warehouse' },
      { key: 'actions', labelKey: 'col_actions', required: true },
    ], p, columnVisibility),
    [p, columnVisibility],
  );
  const qawaemVisibleColumnCount = qawaemColumns.filter((c) => c.checked !== false).length;
  const tableColSpan = achatsTableColSpan(qawaemColumns);

  const importExportProps = useMemo(
    () => buildAchatsImportExportProps({
      showImportDropdown,
      setShowImportDropdown,
      showExportDropdown,
      setShowExportDropdown,
      importDropdownRef,
      exportDropdownRef,
      p,
      t,
      isGeneratingPDF,
      handlers: {
        onImport: handleImport,
        onExportCSV: handleExport,
        onExportExcel: handleExportExcel,
        onExportPDF: handleExportPDF,
      },
    }),
    [showImportDropdown, showExportDropdown, isGeneratingPDF, p, t],
  );

  const advancedOptionsContent = (
        <div className="space-y-6 pt-6">
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
            <div className="flex space-x-1 overflow-x-auto table-scroll">
              {mainTabs.map((tab) => (
                <button key={tab.id} onClick={() => { setMainViewMode(tab.id); setQuickFilter('all'); setActiveDistributionFilter(null); }} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${mainViewMode === tab.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                  <tab.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{tab.name}</span>
                </button>
              ))}
            </div>
          </div>
          {mainViewMode === 'all_receipts' && (
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
              <div className="flex space-x-1 overflow-x-auto table-scroll">
                {quickFilterButtons.map((filter) => (
                  <button key={filter.id} onClick={() => setQuickFilter(filter.id)} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${quickFilter === filter.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                    <filter.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{filter.name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-white">{distributionInfo.title}</h3>
              {activeDistributionFilter && (
                <button onClick={() => setActiveDistributionFilter(null)} className="text-xs text-white/60 hover:text-white bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-lg transition-all flex items-center gap-1">
                  <XCircle size={14} />{p('reset_filter')}
                </button>
              )}
            </div>
            <div {...getErpOverviewGridAttrs(distributionInfo.data.length, '3')}>{distributionInfo.data.map((item) => (
                <button
                  key={item.name}
                  onClick={() => setActiveDistributionFilter(activeDistributionFilter === item.filterKey ? null : item.filterKey)}
                  className={`bg-white/5 rounded-xl p-4 border transition-all cursor-pointer text-left ${activeDistributionFilter === item.filterKey ? 'border-blue-400/60 bg-blue-500/20 shadow-lg shadow-blue-500/20' : 'border-white/10 hover:border-white/20 hover:bg-white/10'}`}
                >
                  <div className="flex justify-between items-center mb-2">
                    <h4 className={`font-medium ${activeDistributionFilter === item.filterKey ? 'text-white' : 'text-white/80'}`}>{item.name}</h4>
                    <div className={`text-sm rounded-full px-2 py-0.5 ${item.color} text-white force-white font-semibold`}>{item.count}</div>
                  </div>
                  <div className="w-full bg-white/10 h-3 rounded-full overflow-hidden">
                    <div className={`h-3 rounded-full ${item.color} transition-all`} style={{ width: `${(() => {
                      const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                      return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                    })()}%` }}></div>
                  </div>
                  <p className={`mt-2 text-sm ${activeDistributionFilter === item.filterKey ? 'text-white/80' : 'text-white/60'}`}>{p('percent_of_total', { percent: (() => {
                    const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                    return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                  })() })}</p>
                </button>
              ))}
            </div>
          </motion.div>
        </div>
  );

  return (
    <>
      <AchatsConfigNotice
        message={!achatsConfig.enablePurchaseReceipts ? p('notice_receipts_disabled', { defaultValue: 'استلام البضائع معطّل — فعّله من إعدادات المشتريات ← استلام ومرتجعات.' }) : null}
      />
      <FacturationListPageLayout
        title={p('title')}
        subtitle={p('subtitle')}
        icon={PackageCheck}
        stats={listStats}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        filterActions={buildAchatsBulkDeleteFilterAction(selectedRecords.length, handleBulkDelete, { deleteLabel: `${t('common.delete')} (${selectedRecords.length})`, title: p('bulk_delete_confirm', { count: selectedRecords.length }) })}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder={p('search_placeholder', { defaultValue: t('common.search', { defaultValue: 'بحث...' }) })}
        onCreate={() => {
          if (!achatsConfig.enablePurchaseReceipts) {
            alert(p('feature_disabled', { defaultValue: 'استلام البضائع معطّل من إعدادات المشتريات.' }));
            return;
          }
          setEditingRecord(null);
          setFormData({
            receipt_number: generateReceiptNumber(),
            purchase_order_id: '',
            purchase_order_number: '',
            supplier_id: '',
            supplier_name: '',
            receipt_date: new Date().toISOString().split('T')[0],
            product_id: '',
            product_name: '',
            product_code: '',
            ordered_quantity: '',
            received_quantity: '',
            rejected_quantity: '',
            unit_price: '',
            total_value: '',
            status: 'pending',
            quality_check_status: achatsConfig.qualityCheckRequired ? 'pending' : 'passed',
            warehouse_id: '',
            warehouse_name: '',
            notes: '',
          });
          setFormTab('general');
          setShowModal(true);
        }}
        createLabel={p('btn_create')}
        importExport={importExportProps}
        headerActions={
          <button type="button" onClick={loadReceipts} className="refresh-button" title={t('common.refresh', { defaultValue: 'تحديث' })}>
            <RefreshCcw size={20} />
          </button>
        }
        showAdvancedOptions={showAdvancedOptions}
        onToggleAdvancedOptions={() => setShowAdvancedOptions(!showAdvancedOptions)}
        advancedOptionsLabel={p('advanced_options')}
        advancedOptionsContent={advancedOptionsContent}
        controlsStrip={
          !qawaem ? (
            <div className="facturation-list-controls-strip p-4 border-b border-white/10">
              <div className="facturation-list-controls-row flex min-w-0 flex-row flex-wrap items-center gap-2 lg:gap-4">
                {wrapControlsStripSearch(viewMode, (
                  <div className="relative min-w-0 flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-white/50" />
                    <input
                      type="text"
                      placeholder={p('search_placeholder', { defaultValue: t('common.search', { defaultValue: 'بحث...' }) })}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                ))}
                <FacturationViewModeToggle
                  viewMode={viewMode}
                  onViewModeChange={setViewMode}
                  isLight={isDashboardLightMode}
                  cardsTitle={t('common.view_cards', { defaultValue: 'بطاقات' })}
                  tableTitle={t('common.view_table', { defaultValue: 'جدول' })}
                />
              </div>
            </div>
          ) : null
        }
      >
        {viewMode === 'table' && (
          <div className={facturationTableScrollClass(qawaem)}>
            <table className="w-full employees-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <th className={mergeStickySelectClass('px-3 py-3 text-center align-middle')}>
                    <div className="flex justify-center">
                      <QawaemTableCheckbox
                        size="lg"
                        checked={rowSelection.allRowsSelected}
                        indeterminate={rowSelection.someRowsSelected}
                        onChange={handleSelectAll}
                      />
                    </div>
                  </th>
                  {isColumnVisible('receipt_number') ? <TableColumnHeaderFilter {...colHeader('receipt_number', p('col_receipt_number'))} /> : null}
                  {isColumnVisible('supplier_name') ? <TableColumnHeaderFilter {...colHeader('supplier_name', p('col_supplier'))} /> : null}
                  {isColumnVisible('receipt_date') ? <TableColumnHeaderFilter {...colHeader('receipt_date', p('col_receipt_date'))} /> : null}
                  {isColumnVisible('purchase_order_number') ? <TableColumnHeaderFilter {...colHeader('purchase_order_number', p('col_purchase_order'))} /> : null}
                  {isColumnVisible('total_value') ? <TableColumnHeaderFilter {...colHeader('total_value', p('col_amount'))} /> : null}
                  {isColumnVisible('quantities') ? <TableColumnHeaderFilter {...colHeader('quantities', p('col_quantities'))} /> : null}
                  {isColumnVisible('status') ? <TableColumnHeaderFilter {...colHeader('status', p('col_status'), { kind: 'select', options: statusOptions, align: 'center' })} /> : null}
                  {isColumnVisible('warehouse') ? <TableColumnHeaderFilter {...colHeader('warehouse', p('col_warehouse'))} /> : null}
                  <QawaemTableHeaderActionsCell
                    thClassName={ACHATS_TABLE_ACTIONS_TH_CLASS}
                    label={p('col_actions')}
                    columns={qawaemColumns}
                    onToggle={handleToggleColumn}
                    visibleCount={qawaemVisibleColumnCount}
                    totalCount={qawaemColumns.length}
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {displayedReceipts.length > 0 ? (
                  displayedReceipts.map((record, index) => (
                    <tr key={record.id} className={`hover:bg-white/5 transition-colors ${selectedRecords.includes(record.id) ? 'bg-white/10' : ''}`}>
                      <td className={mergeStickySelectClass('px-3 py-4 text-center align-middle')}>
                          <div className="flex justify-center">
                            <QawaemTableCheckbox
                              checked={selectedRecords.includes(record.id)}
                              onChange={() => handleSelectRecord(record.id)}
                            />
                          </div>
                        </td>
                      
                      {isColumnVisible('receipt_number') ? (
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold">{record.receipt_number}</span>
                          <span className="text-white/50 text-xs mt-0.5">{p('created_label')} {record.created_at ? fmtDate(record.created_at) : '-'}</span>
                        </div>
                      </td>
                      ) : null}
                      
                      {isColumnVisible('supplier_name') ? (
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold">{record.supplier_name}</span>
                          <span className="text-white/50 text-xs mt-0.5">ID: {record.supplier_id || 'N/A'}</span>
                        </div>
                      </td>
                      ) : null}
                      
                      {isColumnVisible('receipt_date') ? (
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <div className="flex items-center gap-1.5 text-white font-semibold">
                            <Clock size={14} className="text-white/50" />
                            {record.receipt_date ? fmtDate(record.receipt_date) : '-'}
                          </div>
                          <span className="text-white/50 text-xs mt-0.5">{record.receipt_date ? p('days_ago', { days: Math.floor((Date.now() - new Date(record.receipt_date).getTime()) / (1000 * 60 * 60 * 24)) }) : ''}</span>
                        </div>
                      </td>
                      ) : null}
                      
                      {isColumnVisible('purchase_order_number') ? (
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold">{record.purchase_order_number || '-'}</span>
                          <span className="text-white/50 text-xs mt-0.5">ID: {record.purchase_order_id || 'N/A'}</span>
                        </div>
                      </td>
                      ) : null}
                      
                      {isColumnVisible('total_value') ? (
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold">{formatBrandMoney((record.total_value || 0))}</span>
                          <span className="text-white/50 text-xs mt-0.5">{record.product_name || ''}</span>
                        </div>
                      </td>
                      ) : null}
                      
                      {isColumnVisible('quantities') ? (
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold">{record.received_quantity ?? 0} / {record.ordered_quantity ?? 0}</span>
                          <span className="text-white/50 text-xs mt-0.5">{p('label_quality_check')}: {getQualityLabel(record.quality_check_status)}</span>
                        </div>
                      </td>
                      ) : null}
                      
                      {isColumnVisible('status') ? (
                      <td className="px-6 py-4 text-center"><div className="flex justify-center"><QawaemClickableStatusBadge status={record.status} recordId={record.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status')} /></div></td>
                      ) : null}
                      
                      {isColumnVisible('warehouse') ? (
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold">{record.warehouse_name || '-'}</span>
                          <span className="text-white/50 text-xs mt-0.5">{record.rejected_quantity ? `${p('label_rejected_qty')}: ${record.rejected_quantity}` : ''}</span>
                        </div>
                      </td>
                      ) : null}
                      
                      <td className="px-6 py-4">
                        <QawaemErpRowActions
                          onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                          onEdit={() => handleEdit(record)}
                          onDelete={() => handleDelete(record.id)}
                          viewTitle={p('action_view')}
                          editTitle={p('action_edit')}
                          deleteTitle={p('action_delete')}
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan={tableColSpan} className="text-center py-12 text-white/60"><PackageCheck size={48} className="mx-auto mb-4 text-white/30" /><p className="text-lg">{p('empty_state')}</p></td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {viewMode === 'cards' && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {displayedReceipts.length > 0 ? (
              displayedReceipts.map((record, index) => (
                <motion.div key={record.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }} className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-4 mb-4">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2"><PackageCheck size={20} className="text-emerald-400" />{record.receipt_number}</h3>
                        <div className="flex"><QawaemClickableStatusBadge status={record.status} recordId={record.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status')} /></div>
                        {record.receipt_date && (<span className="px-2 py-1 rounded-full text-xs font-medium bg-white/20 text-white/80">{fmtDate(record.receipt_date)}</span>)}
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_return_info')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_receipt_number')}</span> {record.receipt_number}</div>
                            <div><span className="font-medium">{p('label_date')}</span> {record.receipt_date ? fmtDate(record.receipt_date) : '-'}</div>
                            <div><span className="font-medium">{p('label_amount')}</span> {formatBrandMoney((record.total_value || 0))}</div>
                          </div>
                        </div>
                        
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_supplier')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_name')}</span> {record.supplier_name}</div>
                            <div><span className="font-medium">{p('label_id')}</span> {record.supplier_id || 'N/A'}</div>
                          </div>
                        </div>
                        
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_reason_refund')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_quality_check')}:</span> {getQualityLabel(record.quality_check_status)}</div>
                            <div><span className="font-medium">{p('col_warehouse')}:</span> {record.warehouse_name || '-'}</div>
                          </div>
                        </div>
                        
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_origin_order')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_number')}</span> {record.purchase_order_number || '-'}</div>
                            {record.notes && (
                              <div className="mt-2 p-2 bg-white/5 rounded">
                                <span className="font-medium text-white/80 text-xs">{p('label_notes')}</span>
                                <p className="text-white/60 text-xs mt-1">{record.notes}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <QawaemErpRowActions
                      compact
                      size={14}
                      onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                      onEdit={() => handleEdit(record)}
                      onDelete={() => handleDelete(record.id)}
                      viewTitle={p('action_view')}
                      editTitle={p('action_edit')}
                      deleteTitle={p('action_delete')}
                    />
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full text-center py-12"><PackageCheck size={48} className="mx-auto mb-4 text-white/30" /><p className="text-white/60 text-lg">{p('empty_state')}</p></div>
            )}
          </div>
        )}
      </FacturationListPageLayout>

      {showViewModal && viewingRecord ? (
      <QawaemErpViewModal
        open
        onClose={() => setShowViewModal(false)}
        title={p('view_modal_title')}
        icon={Eye}
        testId="achats-view-modal"
      >
        <div className="space-y-4">
                <div className={m.viewSection}>
                  <h3 className={m.viewSectionTitle}><PackageCheck size={18} className="text-emerald-400" />{p('section_return_info')}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div><p className={m.viewLabel}>{p('label_receipt_number_field')}</p><p className={m.viewValueLg}>{viewingRecord.receipt_number}</p></div>
                    <div><p className={m.viewLabel}>{p('col_receipt_date')}</p><p className={m.viewValue}>{viewingRecord.receipt_date ? fmtDate(viewingRecord.receipt_date) : '-'}</p></div>
                    <div><p className={m.viewLabel}>{p('col_amount')}</p><p className={m.viewValueLg}>{formatBrandMoney((viewingRecord.total_value || 0))}</p></div>
                    <div><p className={m.viewLabel}>{p('col_status')}</p><div className="flex">{getStatusBadge(viewingRecord.status)}</div></div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className={m.viewSection}>
                    <h3 className={m.viewSectionTitle}><Building2 size={18} className="text-blue-400" />{p('section_supplier')}</h3>
                    <div className="space-y-3">
                      <div><p className={m.viewLabel}>{p('label_name').replace(':', '')}</p><p className={m.viewValue}>{viewingRecord.supplier_name}</p></div>
                      <div><p className={m.viewLabel}>{p('label_supplier_id')}</p><p className={m.viewValue}>{viewingRecord.supplier_id || 'N/A'}</p></div>
                    </div>
                  </div>

                  <div className={m.viewSection}>
                    <h3 className={m.viewSectionTitle}><FileText size={18} className="text-yellow-400" />{p('section_reason_refund')}</h3>
                    <div className="space-y-3">
                      <div><p className={m.viewLabel}>{p('label_quality_check')}</p><p className={m.viewValue}>{getQualityLabel(viewingRecord.quality_check_status)}</p></div>
                      <div><p className={m.viewLabel}>{p('col_warehouse')}</p><p className={m.viewValue}>{viewingRecord.warehouse_name || '-'}</p></div>
                      {viewingRecord.notes && (<div><p className={m.viewLabel}>{p('label_notes').replace(':', '')}</p><p className="text-white/80 text-sm bg-white/5 p-3 rounded-lg">{viewingRecord.notes}</p></div>)}
                    </div>
                  </div>
                </div>
        </div>
      </QawaemErpViewModal>
      ) : null}

      <QawaemErpCrudModal
        open={showModal}
        onClose={() => resetForm()}
        title={editingRecord ? p('modal_edit_title') : p('modal_new_title')}
        icon={PackageCheck}
        formId="receipt-form"
        saveLabel={t('common.save')}
        onSubmit
        testId="achats-crud-modal"
      >
        <div className={ACHATS_FORM_MODAL_SHELL}>
          <AchatsFormModalTabs
            tabs={formTabs}
            activeTab={formTab}
            onTabChange={setFormTab}
            isLight={m.isLight}
            ariaLabel={editingRecord ? p('modal_edit_title') : p('modal_new_title')}
          />
          <div className={ACHATS_FORM_TAB_PANEL}>
            <form id="receipt-form" onSubmit={handleSubmit}>
                {formTab === 'general' && (
                <div className={ACHATS_FORM_GRID}>
                          <div>
                            <label className={cfgTheme.label}>{p('label_receipt_number_field')}</label>
                            <input type="text" value={formData.receipt_number} onChange={(e) => setFormData({ ...formData, receipt_number: e.target.value })} className={cfgTheme.input} placeholder={p('placeholder_auto_generated')} />
                          </div>
                          <div>
                            <label className={cfgTheme.label}><RequiredLabelText text={p('label_receipt_date_required')} /></label>
                            <input type="date" required value={formData.receipt_date} onChange={(e) => setFormData({ ...formData, receipt_date: e.target.value })} className={cfgTheme.input} />
                          </div>
                          <div>
                            <label className={cfgTheme.label}><RequiredLabelText text={p('label_status_required')} /></label>
                            <div className="relative" ref={statusDropdownRef}>
                              <button type="button" data-status-dropdown-button="true" onClick={() => setShowStatusDropdown(!showStatusDropdown)} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all flex items-center justify-between">
                                <span>{statusOptions.find(s => s.value === formData.status)?.label || p('select')}</span>
                                <ChevronDown size={16} className={`transition-transform ${showStatusDropdown ? 'rotate-180' : ''}`} />
                              </button>
                              {showStatusDropdown && ReactDOM.createPortal(
                                <div data-status-dropdown="true" className="border border-blue-400/60 rounded-2xl shadow-2xl overflow-hidden" style={{ backgroundImage: 'linear-gradient(to bottom right, rgb(51, 65, 85), rgb(29, 71, 196), rgb(51, 65, 85))', position: 'fixed', ...(statusDropdownRef.current && (() => {
                                  const rect = statusDropdownRef.current.getBoundingClientRect();
                                  const spaceBelow = window.innerHeight - rect.bottom;
                                  const spaceAbove = rect.top;
                                  const dropdownHeight = 180;
                                  return spaceBelow < dropdownHeight && spaceAbove > spaceBelow ? { bottom: window.innerHeight - rect.top + 8, left: rect.left, width: rect.width } : { top: rect.bottom + 8, left: rect.left, width: rect.width };
                                })()), zIndex: 99999, boxShadow: '0 8px 32px rgba(59, 130, 246, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.2)' }}>
                                  {statusOptions.map(option => (
                                    <button key={option.value} type="button" onClick={() => { setFormData({ ...formData, status: option.value }); setShowStatusDropdown(false); }} className="w-full flex items-center px-4 py-2.5 hover:bg-blue-500/20 transition-all text-left text-white text-sm font-medium">
                                      <option.icon size={16} className="mr-2" />{option.label}
                                    </button>
                                  ))}
                                </div>,
                                document.body
                              )}
                            </div>
                          </div>
                          <div>
                            <label className={cfgTheme.label}><RequiredLabelText text={p('label_supplier_name_required')} /></label>
                            {loadingSuppliers ? (
                              <div className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white/50">{p('select')}</div>
                            ) : (
                              <CustomSelect
                                value={formData.supplier_id}
                                onChange={(value) => handleSupplierSelection(value)}
                                options={availableSuppliers.map(supplier => ({
                                  value: supplier.id,
                                  label: `${supplier.company_name || supplier.name || p('col_supplier')} - ${supplier.contact_person || supplier.phone1 || ''}`
                                }))}
                                placeholder={p('placeholder_supplier_name')}
                                searchable
                              />
                            )}
                          </div>
                          <div>
                            <label className={cfgTheme.label}>{p('label_supplier_id')}</label>
                            <input type="number" value={formData.supplier_id} onChange={(e) => setFormData({ ...formData, supplier_id: e.target.value })} className={cfgTheme.input} placeholder="ID" />
                          </div>
                          <div>
                            <label className={cfgTheme.label}>{p('label_origin_order_number')}</label>
                            {loadingOrders ? (
                              <div className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white/50">{p('select')}</div>
                            ) : (
                              <CustomSelect
                                value={formData.purchase_order_id}
                                onChange={(value) => handleOrderSelection(value)}
                                options={availablePurchaseOrders.map(order => ({
                                  value: order.id,
                                  label: `${order.order_number || order.id} - ${order.supplier_name || ''}`
                                }))}
                                placeholder={p('ph_purchase_order_number')}
                                searchable
                              />
                            )}
                          </div>
                          <div>
                            <label className={cfgTheme.label}>{p('label_origin_order_id')}</label>
                            <input type="number" value={formData.purchase_order_id} onChange={(e) => setFormData({ ...formData, purchase_order_id: e.target.value })} className={cfgTheme.input} placeholder="ID" />
                          </div>
                          <div>
                            <label className={cfgTheme.label}><RequiredLabelText text={t('modules.achats.pages.demandesAchatPage.col_product', { defaultValue: 'المنتج' })} required /></label>
                            {loadingProducts ? (
                              <div className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white/50">{p('select')}</div>
                            ) : (
                              <CustomSelect
                                value={formData.product_id}
                                onChange={(value) => handleProductSelection(value)}
                                options={productOptions}
                                placeholder={t('modules.achats.pages.demandesAchatPage.col_product', { defaultValue: 'المنتج' })}
                                searchable
                                required
                              />
                            )}
                          </div>
                          <div>
                            <label className={cfgTheme.label}>{t('modules.achats.pages.catalogueFournisseurPage.col_product_code', { defaultValue: 'رمز المنتج' })}</label>
                            <input type="text" value={formData.product_code} onChange={(e) => setFormData({ ...formData, product_code: e.target.value })} className={cfgTheme.input} />
                          </div>
                        </div>
              )}

                {formTab === 'details' && (
                <div className={ACHATS_FORM_GRID}>
                          <div>
                            <label className={cfgTheme.label}>{t('modules.achats.pages.comparatifOffresPage.field_qty', { defaultValue: 'الكمية المطلوبة' })}</label>
                            <input type="number" min="0" step="0.01" value={formData.ordered_quantity} onChange={(e) => setFormData({ ...formData, ordered_quantity: e.target.value })} className={cfgTheme.input} placeholder="0" />
                          </div>
                          <div>
                            <label className={cfgTheme.label}>{t('modules.achats.pages.commandesFournisseursPage.col_qty', { defaultValue: 'الكمية المستلمة' })}</label>
                            <input type="number" min="0" step="0.01" value={formData.received_quantity} onChange={(e) => {
                              const received = e.target.value;
                              const unitPrice = parseFloat(formData.unit_price) || 0;
                              setFormData({
                                ...formData,
                                received_quantity: received,
                                total_value: received && unitPrice ? (parseFloat(received) * unitPrice).toFixed(2) : formData.total_value,
                              });
                            }} className={cfgTheme.input} placeholder="0" />
                          </div>
                          <div>
                            <label className={cfgTheme.label}>{p('label_rejected_qty')}</label>
                            <input type="number" min="0" step="0.01" value={formData.rejected_quantity} onChange={(e) => setFormData({ ...formData, rejected_quantity: e.target.value })} className={cfgTheme.input} placeholder="0" />
                          </div>
                          <div>
                            <label className={cfgTheme.label}>{t('modules.achats.pages.commandesFournisseursPage.col_unit_price_short', { defaultValue: 'سعر الوحدة' })}</label>
                            <input type="number" step="0.01" value={formData.unit_price} onChange={(e) => {
                              const unitPrice = e.target.value;
                              const received = parseFloat(formData.received_quantity) || 0;
                              setFormData({
                                ...formData,
                                unit_price: unitPrice,
                                total_value: received && unitPrice ? (received * parseFloat(unitPrice)).toFixed(2) : formData.total_value,
                              });
                            }} className={cfgTheme.input} placeholder="0.00" />
                          </div>
                          <div>
                            <label className={cfgTheme.label}>{p('label_total_value')}</label>
                            <input type="number" step="0.01" value={formData.total_value} onChange={(e) => setFormData({ ...formData, total_value: e.target.value })} className={cfgTheme.input} placeholder="0.00" />
                          </div>
                          <div>
                            <label className={cfgTheme.label}><RequiredLabelText text={p('label_quality_check_status_required')} /></label>
                            <div className="relative" ref={reasonDropdownRef}>
                              <button type="button" data-reason-dropdown-button="true" onClick={() => setShowReasonDropdown(!showReasonDropdown)} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all flex items-center justify-between">
                                <span>{getQualityLabel(formData.quality_check_status)}</span>
                                <ChevronDown size={16} className={`transition-transform ${showReasonDropdown ? 'rotate-180' : ''}`} />
                              </button>
                              {showReasonDropdown && ReactDOM.createPortal(
                                <div data-reason-dropdown="true" className="border border-blue-400/60 rounded-2xl shadow-2xl overflow-hidden" style={{ backgroundImage: 'linear-gradient(to bottom right, rgb(51, 65, 85), rgb(29, 71, 196), rgb(51, 65, 85))', position: 'fixed', ...(reasonDropdownRef.current && (() => {
                                  const rect = reasonDropdownRef.current.getBoundingClientRect();
                                  return { top: rect.bottom + 8, left: rect.left, width: rect.width };
                                })()), zIndex: 99999, boxShadow: '0 8px 32px rgba(59, 130, 246, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.2)' }}>
                                  {qualityCheckOptions.map(option => (
                                    <button key={option.value} type="button" onClick={() => { setFormData({ ...formData, quality_check_status: option.value }); setShowReasonDropdown(false); }} className="w-full flex items-center px-4 py-2.5 hover:bg-blue-500/20 transition-all text-left text-white text-sm font-medium">{option.label}</button>
                                  ))}
                                </div>,
                                document.body
                              )}
                            </div>
                          </div>
                          <div>
                            <label className={cfgTheme.label}>{p('col_warehouse')}</label>
                            {loadingWarehouses ? (
                              <div className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white/50">{p('select')}</div>
                            ) : (
                              <CustomSelect
                                value={formData.warehouse_id}
                                onChange={(value) => handleWarehouseSelection(value)}
                                options={availableWarehouses.map((warehouse) => ({
                                  value: warehouse.id,
                                  label: warehouse.name || warehouse.warehouse_name || warehouse.id,
                                }))}
                                placeholder={p('placeholder_warehouse_name')}
                                searchable
                              />
                            )}
                          </div>
                          <div className="md:col-span-3">
                            <label className={cfgTheme.label}>{p('label_notes').replace(':', '')}</label>
                            <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all resize-none" rows="4" placeholder={p('placeholder_notes')} />
                          </div>
                        </div>
              )}

        </form>
          </div>
        </div>
      </QawaemErpCrudModal>
      <AchatsCrudNotificationToast notifyToast={notifyToast} onDismiss={dismissNotify} />
    </>
  );
};

export default ReceptionsAchatsPage;




